
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Check, Target, Sparkles, User, ArrowLeft, Users, Search, Briefcase, Wallet, Compass, Flame, Anchor, Wind, ShieldCheck, Globe, HandHeart, Plane, Mic, Camera, PenLine, Upload, FileText, Loader, Brain, Heart, Star, Zap } from 'lucide-react';
import { LifeContext, Language } from '../types';
import { trackPageView, trackOnboardingStep, trackOnboardingComplete } from '../src/lib/analytics';
import { ONBOARDING_QUESTIONS, BULK_UPLOAD_STRINGS } from '../constants/onboardingQuestions';
import ProfileRevealScreen from '../components/ProfileRevealScreen';
import AnalysisLoadingOverlay from '../components/AnalysisLoadingOverlay';


interface OnboardingViewProps {
  onFinish: (data: LifeContext) => void;
  initialLanguage?: Language;
}

const LANGUAGES: {id: Language, label: string, flag: string}[] = [
  { id: 'English', label: 'English', flag: '🇺🇸' },
  { id: 'Spanish', label: 'Español', flag: '🇪🇸' },
  { id: 'Italian', label: 'Italiano', flag: '🇮🇹' },
  { id: 'French', label: 'Français', flag: '🇫🇷' },
  { id: 'German', label: 'Deutsch', flag: '🇩🇪' },
];

const CONTENT = {
  Italian: {
    brandTagline: "Il tuo compagno AI che ti aiuta a capire chi sei e dove stai andando.",
    brandMotto: "Capire. Decidere. Crescere.",
    welcome: "Ciao, sono il tuo compagno di vita.",
    welcomeSub: "Non sono un assistente robotico. Sono uno specchio che ti aiuta a ricordare chi vuoi essere davvero quando il rumore del mondo si fa troppo forte.",
    nameTitle: "Come posso chiamarti?",
    nameSub: "Iniziamo con naturalezza.",
    finalTitle: "Inizia a tracciare",
    finalSub: "Il primo passo è semplice: raccontami qualcosa che ti è successo oggi. Un momento, un pensiero, una decisione. Più mi condividi, più ti capirò.",
    finalCta: "Traccia il tuo primo momento",
    howToTrack: "Come funziona",
    howToTrackItems: [
      "Scrivi cosa ti è successo o cosa stai pensando",
      "Usa la voce se preferisci parlare",
      "Aggiungi una foto per catturare il contesto"
    ],
    start: "Inizia",
    next: "Continua",
    back: "Indietro",
    finish: "Inizia ora",
    goals: [
      { label: 'Salute & Vitalità', icon: <Flame size={18} className="text-rose-500" /> },
      { label: 'Focus & Carriera', icon: <Briefcase size={18} className="text-blue-500" /> },
      { label: 'Libertà Finanziaria', icon: <Wallet size={18} className="text-emerald-500" /> },
      { label: 'Relazioni Vere', icon: <Users size={18} className="text-purple-500" /> },
      { label: 'Pace Interiore', icon: <Wind size={18} className="text-sky-400" /> },
      { label: 'Esplorazione', icon: <Plane size={18} className="text-orange-500" /> },
      { label: 'Creatività', icon: <Sparkles size={18} className="text-pink-500" /> },
    ],
    values: [
      { name: 'Integrità', desc: 'Agire con coerenza anche quando sei solo.' },
      { name: 'Coraggio', desc: 'Affrontare ciò che ti spaventa per crescere.' },
      { name: 'Compassione', desc: 'Trattare te e gli altri con vera umanità.' },
      { name: 'Curiosità', desc: 'Mantenere lo stupore di fronte all\'ignoto.' },
      { name: 'Disciplina', desc: 'Onorare le promesse che fai a te stesso.' },
      { name: 'Libertà', desc: 'Scegliere il tuo tempo e il tuo spazio.' },
      { name: 'Gratitudine', desc: 'Riconoscere la bellezza in ciò che hai.' },
      { name: 'Evoluzione', desc: 'Accettare il cambiamento come unica costante.' },
      { name: 'Equilibrio', desc: 'Proteggere la tua energia dal caos.' }
    ]
  },
  English: {
    brandTagline: "Your AI companion helping you understand who you are and where you're going.",
    brandMotto: "Understand. Decide. Grow.",
    welcome: "Hi, I'm your life companion.",
    welcomeSub: "I'm not a robot assistant. I'm a mirror helping you remember who you truly want to be when the world gets too loud.",
    nameTitle: "What should I call you?",
    nameSub: "Let's keep it simple and natural.",
    finalTitle: "Start tracking",
    finalSub: "The first step is simple: tell me something that happened to you today. A moment, a thought, a decision. The more you share, the better I'll understand you.",
    finalCta: "Track your first moment",
    howToTrack: "How it works",
    howToTrackItems: [
      "Write what happened or what you're thinking",
      "Use voice if you prefer to speak",
      "Add a photo to capture context"
    ],
    start: "Get Started",
    next: "Continue",
    back: "Back",
    finish: "Start now",
    goals: [
      { label: 'Health & Vitality', icon: <Flame size={18} className="text-rose-500" /> },
      { label: 'Focus & Career', icon: <Briefcase size={18} className="text-blue-500" /> },
      { label: 'Financial Freedom', icon: <Wallet size={18} className="text-emerald-500" /> },
      { label: 'Deep Relationships', icon: <Users size={18} className="text-purple-500" /> },
      { label: 'Inner Peace', icon: <Wind size={18} className="text-sky-400" /> },
      { label: 'Exploration', icon: <Plane size={18} className="text-orange-500" /> },
      { label: 'Creativity', icon: <Sparkles size={18} className="text-pink-500" /> },
    ],
    values: [
      { name: 'Integrity', desc: 'Acting consistently even when you\'re alone.' },
      { name: 'Courage', desc: 'Facing what scares you to grow.' },
      { name: 'Compassion', desc: 'Treating yourself and others with true humanity.' },
      { name: 'Curiosity', desc: 'Keeping wonder alive in front of the unknown.' },
      { name: 'Discipline', desc: 'Honoring the promises you make to yourself.' },
      { name: 'Freedom', desc: 'Choosing your own time and space.' },
      { name: 'Gratitude', desc: 'Recognizing beauty in what already is.' },
      { name: 'Evolution', desc: 'Embracing change as the only constant.' },
      { name: 'Balance', desc: 'Protecting your energy from the chaos.' }
    ]
  },
  Spanish: {
    brandTagline: "Tu compañero AI que te ayuda a entender quién eres y hacia dónde vas.",
    brandMotto: "Entender. Decidir. Crecer.",
    welcome: "Hola, soy tu compañero de vida.",
    welcomeSub: "No soy un asistente robótico. Soy un espejo que te ayuda a recordar quién quieres ser realmente cuando el ruido del mundo es demasiado fuerte.",
    nameTitle: "¿Cómo quieres que te llame?",
    nameSub: "Empecemos con naturalidad.",
    finalTitle: "Empieza a registrar",
    finalSub: "El primer paso es simple: cuéntame algo que te pasó hoy. Un momento, un pensamiento, una decisión. Cuanto más compartas, mejor te entenderé.",
    finalCta: "Registra tu primer momento",
    howToTrack: "Cómo funciona",
    howToTrackItems: [
      "Escribe lo que pasó o lo que estás pensando",
      "Usa la voz si prefieres hablar",
      "Añade una foto para capturar el contexto"
    ],
    start: "Empezar",
    next: "Continuar",
    back: "Atrás",
    finish: "Empezar ahora",
    goals: [
      { label: 'Salud & Vitalidad', icon: <Flame size={18} className="text-rose-500" /> },
      { label: 'Carrera & Foco', icon: <Briefcase size={18} className="text-blue-500" /> },
      { label: 'Libertad Financiera', icon: <Wallet size={18} className="text-emerald-500" /> },
      { label: 'Relaciones Profundas', icon: <Users size={18} className="text-purple-500" /> },
      { label: 'Paz Interior', icon: <Wind size={18} className="text-sky-400" /> },
      { label: 'Exploración', icon: <Plane size={18} className="text-orange-500" /> },
      { label: 'Creatividad', icon: <Sparkles size={18} className="text-pink-500" /> },
    ],
    values: [
      { name: 'Integridad', desc: 'Actuar con coherencia incluso cuando estás solo.' },
      { name: 'Coraje', desc: 'Enfrentar lo que te asusta para crecer.' },
      { name: 'Compasión', desc: 'Tratarte a ti y a los demás con verdadera humanidad.' },
      { name: 'Curiosidad', desc: 'Mantener el asombro ante lo desconocido.' },
      { name: 'Disciplina', desc: 'Honrar las promesas que te haces a ti mismo.' },
      { name: 'Libertad', desc: 'Elegir tu propio tiempo y espacio.' },
      { name: 'Gratitud', desc: 'Reconocer la belleza en lo que ya tienes.' },
      { name: 'Evolución', desc: 'Aceptar el cambio como única constante.' },
      { name: 'Equilibrio', desc: 'Proteger tu energía del caos.' }
    ]
  },
  German: {
    brandTagline: "Dein KI-Begleiter, der dir hilft zu verstehen, wer du bist und wohin du gehst.",
    brandMotto: "Verstehen. Entscheiden. Wachsen.",
    welcome: "Hallo, ich bin dein Lebensbegleiter.",
    welcomeSub: "Ich bin kein Roboter. Ich bin ein Spiegel, der dir hilft, dich daran zu erinnern, wer du wirklich sein willst.",
    nameTitle: "Wie darf ich dich nennen?",
    nameSub: "Lass es uns einfach halten.",
    finalTitle: "Starte das Tracking",
    finalSub: "Der erste Schritt ist einfach: Erzähle mir etwas, das dir heute passiert ist. Ein Moment, ein Gedanke, eine Entscheidung. Je mehr du teilst, desto besser verstehe ich dich.",
    finalCta: "Erfasse deinen ersten Moment",
    howToTrack: "So funktioniert es",
    howToTrackItems: [
      "Schreibe, was passiert ist oder was du denkst",
      "Nutze die Stimme, wenn du lieber sprichst",
      "Füge ein Foto hinzu, um den Kontext einzufangen"
    ],
    start: "Starten",
    next: "Weiter",
    back: "Zurück",
    finish: "Jetzt starten",
    goals: [
      { label: 'Gesundheit', icon: <Flame size={18} className="text-rose-500" /> },
      { label: 'Karriere', icon: <Briefcase size={18} className="text-blue-500" /> },
      { label: 'Finanzielle Freiheit', icon: <Wallet size={18} className="text-emerald-500" /> },
      { label: 'Beziehungen', icon: <Users size={18} className="text-purple-500" /> },
      { label: 'Innerer Frieden', icon: <Wind size={18} className="text-sky-400" /> },
      { label: 'Erkundung', icon: <Plane size={18} className="text-orange-500" /> },
      { label: 'Kreativität', icon: <Sparkles size={18} className="text-pink-500" /> },
    ],
    values: [
      { name: 'Integrität', desc: 'Überall dieselbe Person sein.' },
      { name: 'Mut', desc: 'Sich Ängsten stellen.' },
      { name: 'Mitgefühl', desc: 'Menschlichkeit an erste Stelle setzen.' },
      { name: 'Neugier', desc: 'Sich immer "Warum?" fragen.' },
      { name: 'Disziplin', desc: 'Tun, was nötig ist, nicht was einfach ist.' },
      { name: 'Freiheit', desc: 'Die eigene Richtung wählen.' },
      { name: 'Dankbarkeit', desc: 'Den Wert im Bestehenden sehen.' },
      { name: 'Entwicklung', desc: 'Jeden Tag wachsen.' },
      { name: 'Balance', desc: 'Energie vor dem Chaos schützen.' }
    ]
  },
  French: {
    brandTagline: "Ton compagnon IA qui t'aide à comprendre qui tu es et où tu vas.",
    brandMotto: "Comprendre. Décider. Grandir.",
    welcome: "Salut, je suis ton compagnon de vie.",
    welcomeSub: "Je ne suis pas un assistant robotique. Je suis un miroir qui t'aide à te rappeler qui tu veux vraiment être quand le bruit du monde devient trop fort.",
    nameTitle: "Comment puis-je t'appeler ?",
    nameSub: "Commençons naturellement.",
    finalTitle: "Commence à enregistrer",
    finalSub: "La première étape est simple : raconte-moi quelque chose qui t'est arrivé aujourd'hui. Un moment, une pensée, une décision. Plus tu partages, mieux je te comprendrai.",
    finalCta: "Enregistre ton premier moment",
    howToTrack: "Comment ça marche",
    howToTrackItems: [
      "Écris ce qui s'est passé ou ce que tu penses",
      "Utilise la voix si tu préfères parler",
      "Ajoute une photo pour capturer le contexte"
    ],
    start: "Commencer",
    next: "Continuer",
    back: "Retour",
    finish: "Commencer maintenant",
    goals: [
      { label: 'Santé & Vitalité', icon: <Flame size={18} className="text-rose-500" /> },
      { label: 'Focus & Carrière', icon: <Briefcase size={18} className="text-blue-500" /> },
      { label: 'Liberté Financière', icon: <Wallet size={18} className="text-emerald-500" /> },
      { label: 'Relations Vraies', icon: <Users size={18} className="text-purple-500" /> },
      { label: 'Paix Intérieure', icon: <Wind size={18} className="text-sky-400" /> },
      { label: 'Exploration', icon: <Plane size={18} className="text-orange-500" /> },
      { label: 'Créativité', icon: <Sparkles size={18} className="text-pink-500" /> },
    ],
    values: [
      { name: 'Intégrité', desc: 'Agir avec cohérence, même quand tu es seul.' },
      { name: 'Courage', desc: 'Affronter tes peurs pour grandir.' },
      { name: 'Compassion', desc: 'Traiter toi-même et les autres avec humanité.' },
      { name: 'Curiosité', desc: 'Garder l\'émerveillement face à l\'inconnu.' },
      { name: 'Discipline', desc: 'Honorer les promesses que tu te fais.' },
      { name: 'Liberté', desc: 'Choisir ton propre temps et ton espace.' },
      { name: 'Gratitude', desc: 'Reconnaître la beauté de ce que tu as déjà.' },
      { name: 'Évolution', desc: 'Accepter le changement comme seule constante.' },
      { name: 'Équilibre', desc: 'Protéger ton énergie face au chaos.' }
    ]
  },
};

const OnboardingView: React.FC<OnboardingViewProps> = ({ onFinish, initialLanguage }) => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<LifeContext>({
    userName: '',
    goals: [],
    values: [],
    blockers: [],
    setupComplete: false,
    language: initialLanguage || 'English',
    isFirstTimeUser: true,
    momentsTrackedCount: 0
  });

  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<Record<string, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [bulkUploadFiles, setBulkUploadFiles] = useState<{ file: File; preview: string }[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [microInsights, setMicroInsights] = useState<Record<string, string>>({});
  const [showInsight, setShowInsight] = useState(false);
  const [generatedProfile, setGeneratedProfile] = useState<any>(null); // AIProfile from backend
  const [loadingType, setLoadingType] = useState<'questionnaire' | 'photos' | null>(null); // Type of loading overlay

  const lang = formData.language || 'English';
  const c = CONTENT[lang] || CONTENT.English;
  const questions = ONBOARDING_QUESTIONS[lang] || ONBOARDING_QUESTIONS.English;
  const bulkStrings = BULK_UPLOAD_STRINGS[lang] || BULK_UPLOAD_STRINGS.English;

  useEffect(() => {
    trackPageView('/onboarding', 'Onboarding', true);
  }, []);

  const stepNames = ['language', 'welcome', 'name', 'questionnaire', 'bulk_upload', 'profile_reveal', 'final'];

  useEffect(() => {
    trackOnboardingStep(step, stepNames[step] || 'unknown');
    // Scroll to top when step changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const nextStep = async () => {
    if (step === 2 && !formData.userName.trim()) return;

    // Step 3: Process questionnaire before moving to next step
    if (step === 3) {
      if (Object.keys(questionnaireAnswers).length < questions.length) {
        return; // Don't allow skipping questions
      }

      setIsProcessing(true);
      setLoadingType('questionnaire');

      // Immediately show some progress for instant feedback
      setAnalysisProgress(8);

      // Start progress animation with smooth easing
      const startTime = Date.now();
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        setAnalysisProgress(prev => {
          if (prev >= 88) {
            // Slow down near the end to wait for API
            return Math.min(prev + 0.1, 92);
          }
          // Eased progress curve - fast start, gradual slowdown
          // First 2 seconds: 8% -> 50% (fast)
          // Next 3 seconds: 50% -> 75% (medium)
          // After 5 seconds: 75% -> 88% (slow)
          if (elapsed < 2000) {
            const t = elapsed / 2000;
            return 8 + (42 * t * (2 - t)); // Ease-out from 8 to 50
          } else if (elapsed < 5000) {
            const t = (elapsed - 2000) / 3000;
            return 50 + (25 * t); // Linear 50 to 75
          } else if (elapsed < 10000) {
            const t = (elapsed - 5000) / 5000;
            return 75 + (13 * t * (2 - t)); // Slow ease to 88
          }
          return prev;
        });
      }, 50); // Faster interval for smoother animation
      
      try {
        const responses = questions.map(q => ({
          question: q.question,
          answer: questionnaireAnswers[q.id] || ''
        }));

        const response = await fetch('/api/onboarding/process-questionnaire', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ responses, language: lang })
        });

        if (!response.ok) throw new Error('Failed to process questionnaire');

        const data = await response.json();
        setGeneratedProfile(data.aiProfile); // Save AIProfile for reveal screen

        // Complete the progress animation smoothly
        clearInterval(progressInterval);
        setAnalysisProgress(100);
        
        // Brief pause to show completion before transitioning
        setTimeout(() => {
          setIsProcessing(false);
          setLoadingType(null);
          setStep(step + 1);
        }, 800);
      } catch (error) {
        console.error('Questionnaire processing error:', error);
        clearInterval(progressInterval);
        setAnalysisProgress(100);
        setTimeout(() => {
          setIsProcessing(false);
          setLoadingType(null);
          setStep(step + 1); // Continue anyway
        }, 400);
      }
      return;
    }

    // Step 4: Process bulk photo upload before finishing - ONE PHOTO AT A TIME
    if (step === 4) {
      if (bulkUploadFiles.length > 0) {
        setIsProcessing(true);
        setLoadingType('photos');

        // Immediate feedback
        setAnalysisProgress(5);

        const totalPhotos = bulkUploadFiles.length;
        console.log(`📸 Starting sequential upload: ${totalPhotos} photos`);

        let successCount = 0;
        let errorCount = 0;

        // Process photos ONE AT A TIME to avoid timeouts and server overload
        for (let i = 0; i < bulkUploadFiles.length; i++) {
          const file = bulkUploadFiles[i];

          // Update progress based on current photo (5% to 90% range)
          const baseProgress = 5 + ((i / totalPhotos) * 85);
          setAnalysisProgress(baseProgress);

          console.log(`📸 Uploading photo ${i + 1}/${totalPhotos}: ${file.file.name}`);

          try {
            const response = await fetch('/api/onboarding/upload-single-photo', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                base64Data: file.preview,
                language: lang,
                photoIndex: i,
                totalPhotos: totalPhotos
              })
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
              console.error(`❌ Photo ${i + 1} failed: ${errorData.message}`);
              errorCount++;
              // Continue with next photo
            } else {
              const result = await response.json();
              console.log(`✅ Photo ${i + 1} uploaded successfully: entry ${result.entryId}`);
              successCount++;
            }
          } catch (error: any) {
            console.error(`❌ Photo ${i + 1} error:`, error?.message || error);
            errorCount++;
            // Continue with next photo
          }

          // Small delay between photos to avoid overwhelming the server
          if (i < bulkUploadFiles.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }

        console.log(`📸 Sequential upload completed: ${successCount}/${totalPhotos} photos uploaded, ${errorCount} errors`);

        setAnalysisProgress(100);

        setTimeout(() => {
          setIsProcessing(false);
          setLoadingType(null);
          setStep(step + 1);
        }, 600);

        return;
      } else {
        // Skip bulk upload
        console.log('📸 No photos to upload, skipping bulk upload step');
        setStep(step + 1);
        return;
      }
    }

    if (step < 6) {
      setStep(step + 1);
    } else {
      trackOnboardingComplete();
      // Include generatedProfile and all extracted data in the finish callback
      const finalData = { 
        ...formData, 
        userName: formData.userName?.trim() || '',
        language: formData.language || 'English',
        setupComplete: true,
        aiProfile: generatedProfile || undefined,
        // Extract values, goals, blockers, keyPeople, keyPlaces from the profile
        values: generatedProfile?.coreValues?.map((v: any) => v.name) || formData.values,
        goals: generatedProfile?.lifeGoals?.map((g: any) => g.goal) || formData.goals,
        blockers: generatedProfile?.blockers?.map((b: any) => b.blocker) || [],
        keyPeople: generatedProfile?.keyPeople || [],
        keyPlaces: generatedProfile?.keyPlaces || [],
      };
      console.log('📤 Onboarding complete, saving:', { userName: finalData.userName, language: finalData.language });
      onFinish(finalData);
    }
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  // Generate smart micro-insights based on answer content
  const generateMicroInsight = (answer: string, questionId: string): string => {
    const lang = formData.language;
    const wordCount = answer.trim().split(/\s+/).length;
    const hasEmotionWords = /felice|triste|arrabbiato|frustrato|grato|ansioso|sereno|happy|sad|angry|frustrated|grateful|anxious|peaceful|feliz|triste|enojado|frustrado|agradecido|ansioso|tranquilo|glücklich|traurig|wütend|frustriert|dankbar|ängstlich|friedlich|heureux|triste|en colère|frustré|reconnaissant|anxieux|paisible/i.test(answer);
    const hasGoalWords = /voglio|desidero|obiettivo|sogno|aspirazione|want|goal|dream|aspiration|quiero|objetivo|sueño|aspiración|möchte|ziel|traum|aspiration|veux|objectif|rêve/i.test(answer);
    const hasChallengeWords = /difficile|sfida|problema|ostacolo|fatica|difficult|challenge|problem|obstacle|struggle|difícil|desafío|problema|obstáculo|lucha|schwierig|herausforderung|problem|hindernis|kampf|difficile|défi|problème|obstacle/i.test(answer);

    const insights: Record<string, Record<string, string[]>> = {
      Italian: {
        deep: ['🌟 Risposta profonda! Vedo chiarezza nei tuoi pensieri', '💫 Bellissima riflessione, continua così', '✨ Questo mi aiuta a capirti meglio'],
        emotional: ['❤️ Sento le tue emozioni, sono importanti', '💭 Grazie per condividere come ti senti', '🌈 Le emozioni sono la bussola della vita'],
        goal: ['🎯 Obiettivi chiari! Ti aiuterò a raggiungerli', '🚀 Amo la tua determinazione', '⭐ Sogni importanti, ne riparleremo'],
        challenge: ['💪 Vedo la sfida, affrontiamola insieme', '🛡️ Le difficoltà ti rendono più forte', '🌱 Crescerai superando questo'],
        short: ['👍 Perfetto! Ogni dettaglio conta', '✅ Capito! Andiamo avanti', '💡 Interessante, dimmi di più dopo']
      },
      English: {
        deep: ['🌟 Deep answer! I see clarity in your thoughts', '💫 Beautiful reflection, keep going', '✨ This helps me understand you better'],
        emotional: ['❤️ I feel your emotions, they matter', '💭 Thanks for sharing how you feel', '🌈 Emotions are your compass'],
        goal: ['🎯 Clear goals! I\'ll help you reach them', '🚀 I love your determination', '⭐ Important dreams, we\'ll revisit this'],
        challenge: ['💪 I see the challenge, let\'s tackle it', '🛡️ Difficulties make you stronger', '🌱 You\'ll grow overcoming this'],
        short: ['👍 Perfect! Every detail counts', '✅ Got it! Let\'s continue', '💡 Interesting, tell me more later']
      },
      Spanish: {
        deep: ['🌟 ¡Respuesta profunda! Veo claridad en tus pensamientos', '💫 Hermosa reflexión, continúa así', '✨ Esto me ayuda a entenderte mejor'],
        emotional: ['❤️ Siento tus emociones, son importantes', '💭 Gracias por compartir cómo te sientes', '🌈 Las emociones son tu brújula'],
        goal: ['🎯 ¡Objetivos claros! Te ayudaré a alcanzarlos', '🚀 Me encanta tu determinación', '⭐ Sueños importantes, los revisaremos'],
        challenge: ['💪 Veo el desafío, afrontémoslo juntos', '🛡️ Las dificultades te hacen más fuerte', '🌱 Crecerás superando esto'],
        short: ['👍 ¡Perfecto! Cada detalle cuenta', '✅ ¡Entendido! Sigamos', '💡 Interesante, cuéntame más después']
      },
      German: {
        deep: ['🌟 Tiefe Antwort! Ich sehe Klarheit in deinen Gedanken', '💫 Schöne Reflexion, mach weiter', '✨ Das hilft mir, dich besser zu verstehen'],
        emotional: ['❤️ Ich fühle deine Emotionen, sie sind wichtig', '💭 Danke fürs Teilen deiner Gefühle', '🌈 Emotionen sind dein Kompass'],
        goal: ['🎯 Klare Ziele! Ich helfe dir, sie zu erreichen', '🚀 Ich liebe deine Entschlossenheit', '⭐ Wichtige Träume, wir kommen darauf zurück'],
        challenge: ['💪 Ich sehe die Herausforderung, lass sie uns angehen', '🛡️ Schwierigkeiten machen dich stärker', '🌱 Du wirst wachsen, indem du das überwindest'],
        short: ['👍 Perfekt! Jedes Detail zählt', '✅ Verstanden! Weiter geht\'s', '💡 Interessant, erzähl mir später mehr']
      },
      French: {
        deep: ['🌟 Réponse profonde! Je vois de la clarté dans tes pensées', '💫 Belle réflexion, continue', '✨ Cela m\'aide à mieux te comprendre'],
        emotional: ['❤️ Je ressens tes émotions, elles comptent', '💭 Merci de partager ce que tu ressens', '🌈 Les émotions sont ta boussole'],
        goal: ['🎯 Objectifs clairs! Je t\'aiderai à les atteindre', '🚀 J\'adore ta détermination', '⭐ Rêves importants, on en reparlera'],
        challenge: ['💪 Je vois le défi, affrontons-le ensemble', '🛡️ Les difficultés te rendent plus fort', '🌱 Tu grandiras en surmontant cela'],
        short: ['👍 Parfait! Chaque détail compte', '✅ Compris! Continuons', '💡 Intéressant, dis-m\'en plus après']
      }
    };

    const langInsights = insights[lang] || insights.English;

    // Select insight type based on content
    if (wordCount > 40) {
      return langInsights.deep[Math.floor(Math.random() * langInsights.deep.length)];
    } else if (hasEmotionWords) {
      return langInsights.emotional[Math.floor(Math.random() * langInsights.emotional.length)];
    } else if (hasGoalWords) {
      return langInsights.goal[Math.floor(Math.random() * langInsights.goal.length)];
    } else if (hasChallengeWords) {
      return langInsights.challenge[Math.floor(Math.random() * langInsights.challenge.length)];
    } else {
      return langInsights.short[Math.floor(Math.random() * langInsights.short.length)];
    }
  };

  const handleQuestionAnswer = (questionId: string, answer: string) => {
    setQuestionnaireAnswers({ ...questionnaireAnswers, [questionId]: answer });
    // ✅ FIXED: No auto-advance, user controls with Next button
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Generate and show micro-insight
      const currentAnswer = questionnaireAnswers[questions[currentQuestionIndex].id];
      if (currentAnswer) {
        const insight = generateMicroInsight(currentAnswer, questions[currentQuestionIndex].id);
        setMicroInsights({ ...microInsights, [questions[currentQuestionIndex].id]: insight });
        setShowInsight(true);

        // Hide insight and advance after 2 seconds
        setTimeout(() => {
          setShowInsight(false);
          setTimeout(() => {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
          }, 300);
        }, 2000);
      } else {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    }
  };

  // Compress image to reduce size for upload (max 1200px, 80% quality)
  const compressImage = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Calculate new dimensions (max 1200px on longest side)
          const maxSize = 1200;
          let { width, height } = img;

          if (width > height && width > maxSize) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width / height) * maxSize;
            height = maxSize;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to JPEG with 80% quality
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
          resolve(compressedBase64);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = [];
    for (let i = 0; i < Math.min(files.length, 20); i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        try {
          // Compress image before storing
          const preview = await compressImage(file);
          console.log(`📸 Compressed ${file.name}: ${(file.size / 1024).toFixed(0)}KB -> ${(preview.length / 1024).toFixed(0)}KB base64`);
          newFiles.push({ file, preview });
        } catch (err) {
          console.error(`Failed to compress image ${file.name}:`, err);
          // Fallback to original if compression fails
          const reader = new FileReader();
          const preview = await new Promise<string>((resolve) => {
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });
          newFiles.push({ file, preview });
        }
      }
    }

    setBulkUploadFiles([...bulkUploadFiles, ...newFiles].slice(0, 20));
  };

  return (
    <div className="h-full bg-white flex flex-col lg:flex-row safe-top safe-bottom relative overflow-y-auto lg:overflow-hidden font-sans">
      
      {/* Professional Analysis Loading Overlay */}
      <AnalysisLoadingOverlay
        isVisible={isProcessing && loadingType !== null}
        type={loadingType || 'questionnaire'}
        progress={analysisProgress}
        language={lang}
        photoCount={bulkUploadFiles.length}
      />

      {/* Desktop: Left Panel - Branding/Illustration */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-2/5 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex-col justify-between p-12 xl:p-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500 rounded-full blur-[100px]" />
        </div>
        
        <div className="relative z-10">
          <div className="w-16 h-16 bg-white/10 backdrop-blur-xl rounded-2xl flex items-center justify-center mb-8 ring-1 ring-white/20">
            <Compass size={32} className="text-white" />
          </div>
          <h1 className="text-4xl xl:text-5xl font-black text-white tracking-tight leading-tight mb-4">
            Context<br />Graph
          </h1>
          <p className="text-lg text-white/60 font-medium max-w-sm leading-relaxed">
            {c.brandTagline}
          </p>
        </div>
        
        <div className="relative z-10 space-y-6">
          <div className="flex items-center gap-4 text-white/40">
            <div className="flex -space-x-2">
              {['🧘', '💡', '🎯'].map((e, i) => (
                <div key={i} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-lg ring-2 ring-slate-900">
                  {e}
                </div>
              ))}
            </div>
            <span className="text-sm font-medium">{c.brandMotto}</span>
          </div>
          <p className="text-xs text-white/30 font-medium uppercase tracking-widest">
            Privacy First • Context Graph OS
          </p>
        </div>
      </div>
      
      {/* Main Content Area - Desktop uses CSS Grid for predictable layout */}
      <div className="flex-1 flex flex-col px-6 sm:px-8 lg:px-12 xl:px-16 relative min-h-0 lg:grid lg:grid-rows-[auto_1fr_auto] lg:h-full lg:max-h-screen">
        {/* Background blur - mobile only */}
        <div className="lg:hidden absolute top-[-5%] right-[-10%] w-[300px] h-[300px] bg-indigo-50 rounded-full blur-[80px] opacity-70 pointer-events-none" />
        
        {/* Header with progress - Row 1 - Clean single navigation */}
        <header className="h-16 lg:h-16 flex items-center justify-center relative z-20">
          <div className="flex gap-1.5 lg:gap-2">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={`h-1 lg:h-1.5 rounded-full transition-all duration-500 ${step >= i ? 'w-4 lg:w-6 bg-black' : 'w-2 lg:w-3 bg-black/5'}`} />
            ))}
          </div>
        </header>

        {/* Content Container - Row 2 - Uses min-h-0 for proper overflow */}
        <div className="flex-1 relative z-10 overflow-y-auto flex flex-col md:max-w-xl min-h-0 lg:max-w-xl">
          
          {/* Step 0: Language Selection - Compact for desktop */}
          {step === 0 && (
            <div className="flex-1 flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-500 pt-4 md:pt-2 lg:pt-0">
               <div className="shrink-0 text-center lg:text-left mb-3 md:mb-2">
                  <div className="w-12 h-12 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-black rounded-full lg:rounded-lg flex items-center justify-center text-white mx-auto lg:mx-0 mb-2 lg:mb-2 shadow-xl">
                    <Globe size={22} className="md:w-5 md:h-5 lg:w-5 lg:h-5" />
                  </div>
                  <h2 className="text-[24px] md:text-xl lg:text-2xl font-black tracking-tighter mb-1 text-black">Select your language</h2>
               </div>
               <div className="flex-1 pr-2 pb-20 md:pb-16 lg:pb-0">
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2 md:gap-1.5 lg:gap-1.5">
                   {LANGUAGES.map(l => (
                     <button 
                      key={l.id} 
                      onClick={() => { setFormData({...formData, language: l.id}); nextStep(); }}
                      className="w-full p-3 md:p-2.5 lg:p-2.5 bg-gray-50 hover:bg-black hover:text-white rounded-[16px] md:rounded-lg lg:rounded-lg font-bold text-base md:text-sm lg:text-sm transition-all active:scale-95 flex items-center gap-2 border border-black/[0.02] shadow-sm group"
                     >
                       <span className="text-xl md:text-lg lg:text-lg">{l.flag}</span>
                       <span className="flex-1 text-left">{l.label}</span>
                       <ArrowRight size={14} className="md:w-3.5 md:h-3.5 lg:w-3.5 lg:h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                     </button>
                   ))}
                 </div>
               </div>
            </div>
          )}

          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="flex-1 flex flex-col justify-center animate-in fade-in slide-in-from-bottom-8 duration-700 lg:py-0 pb-24 lg:pb-0">
              <div className="w-16 h-16 lg:w-12 lg:h-12 bg-black rounded-[24px] lg:rounded-lg flex items-center justify-center text-white mb-5 lg:mb-3 shadow-2xl">
                <Compass size={28} className="lg:w-5 lg:h-5" />
              </div>
              <h1 className="text-[44px] lg:text-2xl font-black tracking-tighter leading-[0.9] mb-3 lg:mb-2 text-black">
                {c.welcome}
              </h1>
              <p className="text-gray-500 text-[18px] lg:text-sm leading-relaxed font-medium mb-2 max-w-sm">
                {c.welcomeSub}
              </p>
            </div>
          )}

          {/* Step 2: Name Input */}
          {step === 2 && (
            <div className="flex-1 flex flex-col justify-start pt-8 lg:justify-center lg:pt-0 animate-in fade-in slide-in-from-right-8 duration-500 pb-24 lg:pb-0">
              <h2 className="text-[32px] lg:text-xl font-black tracking-tighter mb-2 lg:mb-1 text-black leading-tight">{c.nameTitle}</h2>
              <p className="text-gray-400 mb-6 lg:mb-3 font-medium text-lg lg:text-sm">{c.nameSub}</p>
              <div className="relative border-b-4 border-black/5 focus-within:border-indigo-600 transition-all py-2 lg:max-w-xs">
                <input 
                  autoFocus
                  value={formData.userName}
                  onChange={(e) => setFormData({...formData, userName: e.target.value})}
                  onKeyDown={(e) => e.key === 'Enter' && nextStep()}
                  className="w-full text-[40px] lg:text-2xl font-black outline-none bg-transparent text-black placeholder:text-black/5"
                  placeholder="..."
                />
              </div>
            </div>
          )}

          {/* Step 3: Questionnaire */}
          {step === 3 && (
            <div className="flex-1 flex flex-col animate-in fade-in slide-in-from-right-8 duration-500 pt-2 lg:pt-0 overflow-y-auto pb-28 lg:pb-0">
              {/* Progress Indicator - Mobile Optimized */}
              <div className="mb-4 lg:mb-6 shrink-0">
                <div className="flex items-center gap-2 mb-3 lg:mb-4">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center text-white shadow-xl shrink-0">
                    <Target size={18} className="lg:w-[22px] lg:h-[22px]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg lg:text-2xl font-black tracking-tight text-black leading-none truncate">
                      {lang === 'Italian' ? 'Parliamo di te' : lang === 'Spanish' ? 'Hablemos de ti' : lang === 'German' ? 'Lass uns über dich sprechen' : lang === 'French' ? 'Parlons de toi' : 'Let\'s talk about you'}
                    </h2>
                    <p className="text-gray-500 text-[11px] lg:text-xs font-bold mt-0.5 lg:mt-1">
                      {lang === 'Italian' ? `${currentQuestionIndex + 1} di ${questions.length}` : lang === 'Spanish' ? `${currentQuestionIndex + 1} de ${questions.length}` : lang === 'German' ? `${currentQuestionIndex + 1} von ${questions.length}` : lang === 'French' ? `${currentQuestionIndex + 1} de ${questions.length}` : `${currentQuestionIndex + 1} of ${questions.length}`}
                    </p>
                  </div>
                </div>

                {/* Visual Progress Bar */}
                <div className="w-full h-1.5 lg:h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-500 ease-out"
                    style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Current Question Only - Mobile Optimized */}
              <div className="flex-1 space-y-3 lg:space-y-4">
                <div className="p-4 lg:p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl lg:rounded-2xl border-2 border-indigo-100 animate-in fade-in zoom-in-95 duration-300">
                  <label className="block text-[15px] lg:text-xl font-black text-black mb-3 lg:mb-4 leading-snug">
                    {questions[currentQuestionIndex].question}
                  </label>
                  <textarea
                    autoFocus
                    value={questionnaireAnswers[questions[currentQuestionIndex].id] || ''}
                    onChange={(e) => handleQuestionAnswer(questions[currentQuestionIndex].id, e.target.value)}
                    placeholder={questions[currentQuestionIndex].placeholder}
                    className="w-full min-h-[140px] lg:min-h-[180px] p-3 lg:p-4 bg-white border-2 border-indigo-200 rounded-xl text-[14px] lg:text-base focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 focus:outline-none resize-none font-medium transition-all"
                  />

                  {/* Character Counter */}
                  <div className="flex items-center justify-between mt-3">
                    <span className={`text-xs font-bold ${(questionnaireAnswers[questions[currentQuestionIndex].id] || '').length >= 30 ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {(questionnaireAnswers[questions[currentQuestionIndex].id] || '').length} {lang === 'Italian' ? 'caratteri' : lang === 'Spanish' ? 'caracteres' : lang === 'German' ? 'Zeichen' : lang === 'French' ? 'caractères' : 'characters'}
                    </span>
                    {(questionnaireAnswers[questions[currentQuestionIndex].id] || '').length < 30 && (
                      <span className="text-xs text-amber-600 font-medium">
                        {lang === 'Italian' ? 'Minimo 30 caratteri per continuare' : lang === 'Spanish' ? 'Mínimo 30 caracteres para continuar' : lang === 'German' ? 'Mindestens 30 Zeichen zum Fortfahren' : lang === 'French' ? 'Minimum 30 caractères pour continuer' : 'Minimum 30 characters to continue'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Micro-Insight Display */}
                {showInsight && microInsights[questions[currentQuestionIndex].id] && (
                  <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl animate-in fade-in zoom-in-95 duration-300 shadow-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center shrink-0 shadow-md">
                        <Sparkles size={20} className="text-white" />
                      </div>
                      <p className="text-base font-bold text-emerald-900 leading-snug flex-1">
                        {microInsights[questions[currentQuestionIndex].id]}
                      </p>
                    </div>
                  </div>
                )}

                {/* Previously Answered Questions - Collapsed View */}
                {currentQuestionIndex > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                      {lang === 'Italian' ? 'Risposte precedenti' : lang === 'Spanish' ? 'Respuestas anteriores' : lang === 'German' ? 'Vorherige Antworten' : lang === 'French' ? 'Réponses précédentes' : 'Previous answers'}
                    </p>
                    <div className="space-y-2">
                      {questions.slice(0, currentQuestionIndex).map((q, idx) => (
                        <button
                          key={q.id}
                          onClick={() => setCurrentQuestionIndex(idx)}
                          className="w-full p-3 bg-gray-50 rounded-lg text-left hover:bg-gray-100 transition-all group"
                        >
                          <p className="text-xs font-bold text-gray-600 mb-1">{q.question}</p>
                          <p className="text-xs text-gray-500 line-clamp-1 group-hover:text-indigo-600">
                            {questionnaireAnswers[q.id] || '...'}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Bulk Upload */}
          {step === 4 && (
            <div className="flex-1 flex flex-col animate-in fade-in zoom-in-95 duration-700 pt-4 lg:pt-0 overflow-y-auto pb-28 lg:pb-0">
              <div className="text-center lg:text-left mb-6 shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white mx-auto lg:mx-0 mb-3 shadow-xl">
                  <Upload size={22} />
                </div>
                <h2 className="text-[24px] lg:text-2xl font-black tracking-tight mb-2 text-black">{bulkStrings.title}</h2>
                <p className="text-gray-600 text-sm leading-relaxed font-medium max-w-md mx-auto lg:mx-0">
                  {bulkStrings.subtitle}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                {/* Photo Upload */}
                <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-100">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center shrink-0">
                      <Camera size={18} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-sm text-black mb-1">{bulkStrings.photoTitle}</h3>
                      <p className="text-xs text-gray-600">{bulkStrings.photoDesc}</p>
                    </div>
                  </div>
                  <label className="block">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleFileSelect(e)}
                      className="hidden"
                    />
                    <div className="w-full p-3 bg-white border-2 border-dashed border-indigo-200 rounded-lg text-center cursor-pointer hover:border-indigo-400 transition-colors">
                      <span className="text-sm font-bold text-indigo-600">{bulkStrings.selectPhotos}</span>
                    </div>
                  </label>
                </div>

                {/* Photos Preview */}
                {bulkUploadFiles.length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-xs font-bold text-black/60 mb-2">
                      {bulkStrings.uploaded.replace('{count}', bulkUploadFiles.length.toString())}
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {bulkUploadFiles.map((f, i) => (
                        <div key={i} className="relative w-12 h-12 bg-white rounded-lg border border-gray-200 overflow-hidden">
                          {f.preview && (
                            <img src={f.preview} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Profile Reveal */}
          {step === 5 && generatedProfile && (
            <ProfileRevealScreen
              profile={generatedProfile}
              language={lang}
              onEdit={() => {
                // Handled internally by ProfileRevealScreen with chat editor
              }}
              onConfirm={() => {
                setStep(step + 1);
              }}
              onProfileUpdate={(updatedProfile) => {
                // Update the profile in state as user edits it
                setGeneratedProfile(updatedProfile);
              }}
            />
          )}

          {/* Step 6: Final - Track First Moment CTA */}
          {step === 6 && (
            <div className="flex-1 flex flex-col animate-in fade-in zoom-in-95 duration-700 pt-2 lg:pt-0 overflow-y-auto pb-28 lg:pb-0">
              <div className="text-center lg:text-left mb-4 lg:mb-6 shrink-0">
                <div className="w-12 h-12 lg:w-12 lg:h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl lg:rounded-lg flex items-center justify-center text-white mx-auto lg:mx-0 mb-2 lg:mb-3 shadow-xl">
                   <Sparkles size={22} className="lg:w-5 lg:h-5" />
                </div>
                <h2 className="text-[24px] lg:text-2xl font-black tracking-tight mb-2 lg:mb-1 text-black leading-tight">{c.finalTitle}</h2>
                <p className="text-gray-600 text-[14px] lg:text-sm leading-[1.5] font-medium max-w-[300px] mx-auto lg:mx-0 lg:max-w-md">
                  {c.finalSub}
                </p>
              </div>
              
              <div className="space-y-2 mb-2 lg:mb-6 shrink-0">
                <p className="text-[10px] lg:text-[11px] font-bold text-black/40 uppercase tracking-widest text-center lg:text-left">{c.howToTrack}</p>
                <div className="space-y-2">
                  {[
                    { icon: <PenLine size={16} className="text-indigo-500" />, text: c.howToTrackItems[0] },
                    { icon: <Mic size={16} className="text-rose-500" />, text: c.howToTrackItems[1] },
                    { icon: <Camera size={16} className="text-emerald-500" />, text: c.howToTrackItems[2] }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5 p-3 lg:p-3 bg-gray-50/80 rounded-xl lg:rounded-xl border border-gray-100">
                      <div className="w-8 h-8 lg:w-8 lg:h-8 bg-white rounded-lg lg:rounded-lg flex items-center justify-center shadow-sm shrink-0">
                        {item.icon}
                      </div>
                      <span className="text-[13px] lg:text-[13px] text-black/70 font-medium leading-snug">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with CTA - Row 3 - Fixed at bottom on mobile (hidden for Profile Reveal screen) */}
        <footer className={`fixed bottom-0 left-0 right-0 px-6 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-4 bg-gradient-to-t from-white via-white to-transparent z-50 lg:relative lg:px-0 lg:py-3 lg:bg-none lg:border-t lg:border-gray-100 lg:mt-auto ${step === 5 ? 'pointer-events-none' : ''}`}>
          {step > 0 && step !== 5 && (
            <>
              {step === 4 && bulkUploadFiles.length === 0 && (
                <button
                  onClick={nextStep}
                  className="w-full lg:w-auto lg:min-w-[200px] h-14 lg:h-10 text-black bg-gray-100 rounded-[20px] lg:rounded-lg font-black text-[16px] lg:text-sm flex items-center justify-center gap-2 mb-2 hover:bg-gray-200 transition-all"
                >
                  {bulkStrings.skip}
                  <ArrowRight size={18} strokeWidth={3} className="lg:w-4 lg:h-4" />
                </button>
              )}
              <button
                onClick={() => {
                  if (step === 3 && currentQuestionIndex < questions.length - 1) {
                    handleNextQuestion();
                  } else {
                    nextStep();
                  }
                }}
                disabled={isProcessing || (step === 3 && (questionnaireAnswers[questions[currentQuestionIndex].id] || '').length < 30)}
                className={`w-full lg:w-auto lg:min-w-[200px] h-14 lg:h-10 text-white rounded-[20px] lg:rounded-lg font-black text-[16px] lg:text-sm flex items-center justify-center gap-2 active-scale transition-all shadow-2xl hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed ${
                  step === 6
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                    : step === 3 || step === 4
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                    : 'bg-black hover:bg-gray-900'
                }`}
              >
                {isProcessing ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    {step === 3 ? (lang === 'Italian' ? 'Analisi in corso...' : lang === 'Spanish' ? 'Analizando...' : lang === 'German' ? 'Analysiere...' : lang === 'French' ? 'Analyse...' : 'Analyzing...') : bulkStrings.processing}
                  </>
                ) : (
                  <>
                    {step === 1 ? c.start : step === 6 ? c.finish : step === 3 ? (lang === 'Italian' ? 'Continua' : lang === 'Spanish' ? 'Continuar' : lang === 'German' ? 'Weiter' : lang === 'French' ? 'Continuer' : 'Continue') : step === 4 && bulkUploadFiles.length > 0 ? bulkStrings.upload : c.next}
                    <ArrowRight size={18} strokeWidth={3} className="lg:w-4 lg:h-4" />
                  </>
                )}
              </button>
            </>
          )}
          <p className="text-center lg:text-left mt-3 lg:mt-1 text-[9px] lg:text-[8px] font-black text-black/10 uppercase tracking-widest lg:hidden">
            Privacy First • Context Graph OS
          </p>
        </footer>
      </div>
    </div>
  );
};

export default OnboardingView;
